import { useState, type FormEvent } from 'react';
import { useAuth } from '../lib/authContext';
import type { Translate } from '../lib/i18n';
import { useI18n } from '../lib/use-i18n';

function authMessage(error: unknown, t: Translate): string {
  const code = error && typeof error === 'object' && 'code' in error
    ? String((error as { code?: string }).code || '')
    : '';
  if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') return t('invalidCredentials');
  if (code === 'auth/weak-password') return t('weakPassword');
  return t('genericError');
}

export function AuthView() {
  const { signIn, register } = useAuth();
  const { t } = useI18n();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    if (mode === 'register' && password !== confirmation) {
      setError(t('passwordMismatch'));
      return;
    }
    setLoading(true);
    try {
      if (mode === 'register') await register(email.trim(), password);
      else await signIn(email.trim(), password);
    } catch (reason) {
      setError(authMessage(reason, t));
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setMode((current) => current === 'login' ? 'register' : 'login');
    setConfirmation('');
    setError('');
  };

  return (
    <section className="auth-view">
      <form className="auth-card glass-panel" onSubmit={(event) => void submit(event)}>
        <div className="auth-logo"><img src="/cd-logo-mini.svg" alt="" /></div>
        <h1>{t('account')}</h1>
        {error && <div className="auth-error" role="alert">{error}</div>}
        <label><span>{t('email')}</span><input data-focusable="true" type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" autoFocus required /></label>
        <label><span>{t('password')}</span><input data-focusable="true" type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete={mode === 'register' ? 'new-password' : 'current-password'} minLength={mode === 'register' ? 6 : undefined} required /></label>
        {mode === 'register' && <label><span>{t('repeatPassword')}</span><input data-focusable="true" type="password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} autoComplete="new-password" minLength={6} required /></label>}
        <button className="primary-action auth-submit" data-focusable="true" type="submit" disabled={loading}>{loading ? t('pleaseWait') : mode === 'register' ? t('createAccount') : t('signIn')}</button>
        <button className="auth-switch" data-focusable="true" type="button" onClick={switchMode}>{mode === 'register' ? t('signIn') : t('createAccount')}</button>
      </form>
    </section>
  );
}
